import { ActionReducerMapBuilder, CaseReducer, PayloadAction } from "@reduxjs/toolkit";
import type { AdminState } from "../AdminDb";
import { AdminBusinessListItem } from "../../../interfaces/AdminBusinessListObject";
import {
  extendBusinessSubscription,
  fetchAdminBusinessList,
  fetchAdminNoSubscriptionBusinesses,
} from "../../../services/thunks/AdminBusinessesThunk";
import { AdminTabState, createAdminTabState, registerAsync } from "./adminTabState";

// Admin -> Businesses -> All Businesses. Same file-owns-its-tab arrangement as
// approvals.ts/subscriptions.ts, and the same per-bucket caching Approvals
// uses (one bucket per filter value, fetched only the first time it's opened)
// rather than Subscriptions' "any filter change refetches" - but unlike
// Approvals' buckets, this list is PAGINATED, so each bucket also tracks its
// own last-loaded page and totals rather than just rows + lastFetchedAt.

export type ExpiredKey = "nonExpired" | "expired";

/** The three sub-tabs. "noSubscription" isn't a server-side filter - it's a
 *  full scan of the non-expired listing filtered to subscriptionEnd === null
 *  (see fetchAdminNoSubscriptionBusinesses), so its bucket is always loaded
 *  in one go and never has a "next page". */
export type AllBusinessesFilterKey = ExpiredKey | "noSubscription";

export const bucketKeyOf = (expired: boolean): ExpiredKey =>
  expired ? "expired" : "nonExpired";

interface PagedBucket {
  rows: AdminBusinessListItem[];
  /** Last page successfully loaded, 0-indexed. null = never loaded (same
   *  "never loaded" vs "loaded and empty" distinction StatusBucket uses). */
  page: number | null;
  totalPages: number;
  totalElements: number;
  lastFetchedAt: number | null;
}

export interface AllBusinessesState extends AdminTabState {
  byExpired: Record<AllBusinessesFilterKey, PagedBucket>;
  /** Single-select, like Subscriptions' status chips - exactly one sub-tab is
   *  active at a time. */
  activeExpired: AllBusinessesFilterKey;
  search: string;
}

const emptyBucket = (): PagedBucket => ({
  rows: [],
  page: null,
  totalPages: 0,
  totalElements: 0,
  lastFetchedAt: null,
});

export const allBusinessesInitialState = (): AllBusinessesState => ({
  ...createAdminTabState(),
  byExpired: {
    nonExpired: emptyBucket(),
    expired: emptyBucket(),
    noSubscription: emptyBucket(),
  },
  activeExpired: "nonExpired",
  search: "",
});

/** Fetch key for any sub-tab (loadingMap/errorMap key). */
export const allBusinessesFilterFetchKey = (filter: AllBusinessesFilterKey): string =>
  `fetchBusinessList_${filter}`;

export const allBusinessesExtendKey = (businessId: string): string =>
  `extendSubscription_${businessId}`;

// ── Plain reducers ──────────────────────────────────────────────────────────

const setAllBusinessesExpired: CaseReducer<
  AdminState,
  PayloadAction<AllBusinessesFilterKey>
> = (
  state,
  action
) => {
  state.allBusinesses.activeExpired = action.payload;
};

const setAllBusinessesSearch: CaseReducer<AdminState, PayloadAction<string>> = (
  state,
  action
) => {
  state.allBusinesses.search = action.payload;
};

export const allBusinessesReducers = {
  setAllBusinessesExpired,
  setAllBusinessesSearch,
};

// ── Async cases ─────────────────────────────────────────────────────────────

export function attachAllBusinessesCases(
  builder: ActionReducerMapBuilder<AdminState>
): void {
  registerAsync(builder, fetchAdminBusinessList, {
    tab: (state) => state.allBusinesses,
    key: (arg) => allBusinessesFilterFetchKey(bucketKeyOf(arg.expired)),
    loading: "Loading businesses...",
    onFulfilled: (state, payload) => {
      const bucket = state.allBusinesses.byExpired[bucketKeyOf(payload.expired)];
      bucket.rows = payload.append
        ? [...bucket.rows, ...payload.result.content]
        : payload.result.content;
      bucket.page = payload.result.page;
      bucket.totalPages = payload.result.totalPages;
      bucket.totalElements = payload.result.totalElements;
      bucket.lastFetchedAt = Date.now();
    },
  });

  registerAsync(builder, fetchAdminNoSubscriptionBusinesses, {
    tab: (state) => state.allBusinesses,
    key: () => allBusinessesFilterFetchKey("noSubscription"),
    loading: "Loading businesses...",
    onFulfilled: (state, rows) => {
      const bucket = state.allBusinesses.byExpired.noSubscription;
      bucket.rows = rows;
      // Always the complete set - one "page", nothing more to load.
      bucket.page = 0;
      bucket.totalPages = 1;
      bucket.totalElements = rows.length;
      bucket.lastFetchedAt = Date.now();
    },
  });

  registerAsync(builder, extendBusinessSubscription, {
    tab: (state) => state.allBusinesses,
    key: (arg) => allBusinessesExtendKey(arg.businessId),
    loading: "Extending subscription...",
    onFulfilled: (state, payload) => {
      // Patch subscriptionEnd in place wherever this row currently lives -
      // both buckets are checked because this tab trusts the `expired` query
      // param rather than recomputing expired/not itself (see the plan's open
      // questions), so an extension never moves a row between buckets on its
      // own.
      //
      // Guarded on a truthy newSubscriptionEnd: the backend call already
      // succeeded by the time onFulfilled runs, but convertAdminExtendSubscriptionResult
      // returns null for that field if the response is missing it or uses a
      // field name the convertor doesn't know - patching the row with null in
      // that case would silently wipe a real date to "No subscription" while
      // the UI still reports success. Leaving the row untouched here is safe:
      // AllBusinessesTab's onFulfilled-adjacent success handler falls back to
      // a refetch of the active bucket in that same edge case.
      if (!payload.newSubscriptionEnd) return;
      (["nonExpired", "expired"] as ExpiredKey[]).forEach((key) => {
        const bucket = state.allBusinesses.byExpired[key];
        const row = bucket.rows.find((r) => r.businessId === payload.businessId);
        if (row) row.subscriptionEnd = payload.newSubscriptionEnd;
      });
      // It has a subscription now, so it no longer belongs under
      // "No subscription" - drop it there and keep that count honest.
      const noSub = state.allBusinesses.byExpired.noSubscription;
      const before = noSub.rows.length;
      noSub.rows = noSub.rows.filter((r) => r.businessId !== payload.businessId);
      if (noSub.rows.length < before) {
        noSub.totalElements = Math.max(0, noSub.totalElements - 1);
      }
    },
  });
}

// ── Selectors (`any` state - see the note in approvals.ts) ──────────────────

export const selectAllBusinesses = (state: any): AllBusinessesState =>
  state.admin.allBusinesses;

export const selectAllBusinessesBucket = (state: any): PagedBucket => {
  const tab = state.admin.allBusinesses as AllBusinessesState;
  return tab.byExpired[tab.activeExpired];
};

/** Per-sub-tab counts for the chips. undefined = that sub-tab hasn't loaded
 *  yet (so the chip shows no number rather than a misleading 0). */
export const selectAllBusinessesCounts = (
  state: any
): Record<AllBusinessesFilterKey, number | undefined> => {
  const tab = state.admin.allBusinesses as AllBusinessesState;
  const countOf = (key: AllBusinessesFilterKey) =>
    tab.byExpired[key].lastFetchedAt === null ? undefined : tab.byExpired[key].totalElements;
  return {
    nonExpired: countOf("nonExpired"),
    expired: countOf("expired"),
    noSubscription: countOf("noSubscription"),
  };
};

/** True while more pages remain for the ACTIVE bucket - null totalPages
 *  (never loaded) reads as false, same as an exhausted list. */
export const selectHasMoreAllBusinesses = (state: any): boolean => {
  const bucket = selectAllBusinessesBucket(state);
  return bucket.page !== null && bucket.page + 1 < bucket.totalPages;
};

/** Rows of the active sub-tab with the tab's search applied - client-side,
 *  over whatever pages have been loaded so far (the endpoint has no search
 *  param of its own). */
export const selectVisibleAllBusinesses = (state: any): AdminBusinessListItem[] => {
  const tab = state.admin.allBusinesses as AllBusinessesState;
  const term = tab.search.trim().toLowerCase();
  const rows = tab.byExpired[tab.activeExpired].rows;
  if (!term) return rows;
  return rows.filter(
    (row) =>
      row.businessName.toLowerCase().includes(term) ||
      (row.ownerContactPhone ?? "").includes(term) ||
      (row.ownerContactEmail ?? "").toLowerCase().includes(term)
  );
};

/** True while THIS business's extend-subscription call is in flight - keyed
 *  per row so the rest of the list stays interactive, same pattern as
 *  approvals.ts's selectIsDeciding. */
export const selectIsExtending = (state: any, businessId: string): boolean =>
  !!state.admin.allBusinesses.loadingMap[allBusinessesExtendKey(businessId)];
